import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { AssetSelectionComponent } from '@features/shopping/asset/selection/asset-selection.component';
import { ConfirmLineTerminationComponent } from '@features/shopping/confirm-line-termination/confirm-line-termination.component';
import { LineAsset, UserAsset } from '@models/dashboard.model';
import {
  MULTI_SIM_TYPES,
  calculateItemsToDelete,
  constructAssetTitle
} from '@models/functions.model';
import { ShoppingCartData } from '@models/shopping-cart.model';
import { ClientSettings } from '@models/shopping.model';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityManagerService } from '@services/activity/manager/activity-manager.service';
import { FilterStateService } from '@services/filter-state/filter-state.service';
import { ModalService } from '@services/modal/modal.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'ts-activity-5-line-selection',
  templateUrl: './line-selection.component.html',
  imports: [CommonModule, TranslationPipe, AssetSelectionComponent, ConfirmLineTerminationComponent]
})
export class LineSelectionComponent implements OnInit, OnDestroy {
  @ViewChild('confirmChangeCarrierModal') confirm_change_carrier_template: TemplateRef<any>;
  @ViewChild('multiSimConfirmation') multi_sim_confirmation_template: TemplateRef<any>;
  asset: UserAsset;
  shopping_cart: ShoppingCartData;
  existing_asset: UserAsset | undefined;
  error_modal_line: LineAsset | undefined;
  multi_sim_types: string[] = MULTI_SIM_TYPES;
  open_1LMD_modal: boolean = false;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activity_manager_svc: ActivityManagerService,
    private filter_svc: FilterStateService,
    private modal_svc: ModalService,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.shopping_svc.getShoppingItems().subscribe({
        next: (res: ShoppingCartData) => {
          this.shopping_cart = res;
          this.existing_asset = this.shopping_cart.existing_asset;
        },
        error: (err: HttpErrorResponse) => {}
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  selectAsset(
    asset: UserAsset | LineAsset,
    from_modal: boolean = false,
    is_additional_asset: boolean = false
  ): void {
    if (!is_additional_asset) this.asset = asset as UserAsset;

    this.open_1LMD_modal = false;
    this.error_modal_line = undefined;
    this.modal_svc.dismissAll();

    if (
      [this.multi_sim_types[0], this.multi_sim_types[1], this.multi_sim_types[6]].includes(
        (asset as UserAsset).multi_sim_type
      ) &&
      !from_modal
    )
      this.modal_svc.open(this.multi_sim_confirmation_template, {
        backdrop: 'static',
        centered: true
      });
    else if ((asset as LineAsset).multisim_line) {
      this.error_modal_line = asset as LineAsset;
      this.open_1LMD_modal = true;

      this.modal_svc.open(this.multi_sim_confirmation_template, {
        backdrop: 'static',
        centered: true
      });
    } else if (
      this.shopping_cart &&
      this.shopping_cart.shopping_cart_items.length &&
      this.existing_asset &&
      (this.existing_asset.device.id !== this.asset?.device?.id ||
        (!is_additional_asset && this.existing_asset.line.id !== (asset as UserAsset)?.line?.id) ||
        (is_additional_asset && this.existing_asset.line.id !== (asset as LineAsset).id))
    )
      this.modal_svc.confirmationDialog(
        {
          title: this.translation_svc.getTranslatedModalLabels()[0],
          message: this.translation_svc.getTranslatedModalLabels()[1],
          confirm_button_text: this.translation_svc.translate('continue'),
          cancel_button_text: this.translation_svc.translate('cancel'),
          onConfirm: () => {
            if (is_additional_asset) this.asset.line = asset as LineAsset;

            let ids_to_delete: number[] = calculateItemsToDelete(
              this.shopping_cart.shopping_cart_items
            );
            this.confirmModalClearCart(ids_to_delete);
            this.modal_svc.dismissAll();
          }
        },
        {
          size: 'dialog-centered',
          centered: true,
          backdrop: 'static'
        }
      );
    else {
      if (is_additional_asset) this.asset.line = asset as LineAsset;

      this.clientSettingsCheck();
    }
  }

  changeCarrierContinue(confirm: boolean): void {
    if (confirm) {
      this.modal_svc.dismissAll();

      if (!this.asset?.line?.id || !this.asset.line.carrier_id) return;
      this.activity_manager_svc.state().setParameter('lineId', this.asset.line.id);
      this.activity_manager_svc
        .state()
        .setParameter('excluded_carrier_id', this.asset.line.carrier_id);
      this.activity_manager_svc.state().setParameter('device_type', this.asset.device?.type);
      this.activity_manager_svc.state().saveConfig();
      this.filter_svc.reset();

      this.subscriptions.add(
        this.shopping_svc
          .sendShoppingItems(this.generateCartPayload(), this.existing_asset ? false : true)
          .subscribe({
            next: (res: ShoppingCartData) => {
              this.activity_manager_svc.navigateToNextStep();
            },
            error: (err: HttpErrorResponse) => {}
          })
      );
    }
  }

  calculateModalAdditionalLines(): LineAsset[] {
    return this.asset.additional_lines?.filter((line: LineAsset) => line.status === 'Active')!;
  }

  multisimModalBody(asset: UserAsset): string {
    return asset.multi_sim_type === this.multi_sim_types[0] && !this.open_1LMD_modal
      ? this.translation_svc.translate('multisim_device_is', constructAssetTitle(this.asset, true))
      : this.translation_svc.translate(
          'multisim_change_carrier_line_text',
          this.open_1LMD_modal
            ? this.error_modal_line?.international_number!
            : asset.line.international_number
        );
  }

  multisimModalPrompt(asset: UserAsset): string {
    return asset.multi_sim_type === this.multi_sim_types[0]
      ? this.translation_svc.translate('multisim_select_eligible_line')
      : this.translation_svc.translate('multisim_devices_affected');
  }

  lineInCart(line: LineAsset): boolean {
    return line.id == this.shopping_cart.existing_asset?.line?.id;
  }

  private confirmModalClearCart(ids_to_delete: number[]): void {
    this.subscriptions.add(
      this.shopping_svc.clearSpecifiedCartItems(ids_to_delete).subscribe({
        next: (res: ShoppingCartData) => {
          this.shopping_cart = res;
          this.clientSettingsCheck();
        },
        error: (err: HttpErrorResponse) => {}
      })
    );
  }

  private clientSettingsCheck(): void {
    this.subscriptions.add(
      this.shopping_svc.clientSettings().subscribe({
        next: (res: ClientSettings) => {
          const contract_end_date: Date = new Date(this.asset.line.contract_end_date!);
          const current_date: Date = new Date();
          if (res.notify_user_of_etf && current_date.getTime() <= contract_end_date.getTime()) {
            this.openConfirmationModal();
          } else {
            this.changeCarrierContinue(true);
          }
        },
        error: (err: HttpErrorResponse) => {}
      })
    );
  }

  private generateCartPayload(): ShoppingCartData {
    if (this.existing_asset?.device.id && this.asset.device.id != this.existing_asset?.device.id)
      this.shopping_cart.device_include_admin_options =
        this.shopping_cart.accessories_include_admin_options = undefined;

    return {
      ...this.shopping_cart,
      country_id:
        this.asset?.line.country_id ?? this.activity_manager_svc.state().parameter('country')?.id,
      orderTypeId: this.activity_manager_svc.currentOrderTypeId(),
      lineId: this.activity_manager_svc.state().parameter('lineId'),
      shopping_cart_items: [],
      existing_asset: {
        ...this.asset,
        asset_from_inventory: true,
        asset_selection_case: 1
      },
      progress_stepper: this.activity_manager_svc
        .state()
        .currentStep(this.activity_manager_svc.state().activeStepIndex())
    };
  }

  private openConfirmationModal(): void {
    this.modal_svc.open(this.confirm_change_carrier_template, {
      backdrop: 'static',
      centered: true
    });
  }
}
